import type { DeviceLabel } from "@/_store/devices-settings.module";
import { DevicePolicyType, OsType } from "@/constants/devices";
import { defineStore } from "pinia";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import type { AxiosRequestConfig } from "axios";
import { StoreModule } from "@/constants/store";
import type { IntervalUnit, RepeatInterval } from "@/constants/device-policy";
import {
  convertFileIntegrityPolicyForBackend,
  mapFileIntegrityPoliciesToTableItems,
  mapFileIntegrityResponseToItems,
} from "@/_store/device-policy/adapters";

// Mirrors the API contract, which groups policies under a `fileIntegrity` key rather than
// tagging each item — so there is no policyType here.
export interface DevicePolicyFileIntegrityItem {
  id?: string;
  os: OsType; // WINDOWS | OSX
  repeatEvery: number; // >= 1
  intervalUnit: IntervalUnit;
  monitoredPaths: string[]; // >= 1 absolute path
  labels: DeviceLabel[];
}

export interface FileIntegrityPolicyPayload {
  os: OsType;
  repeatInterval: RepeatInterval;
  repeatFrequency: number;
  monitoredPaths: string[];
  labels: DeviceLabel[];
}

export type DevicePolicyItem = DevicePolicyFileIntegrityItem;

export interface DevicePolicyAdaptedItem {
  tableItemId: string;
  policyType: DevicePolicyType;
  display: Record<string, string>; // text-cell values keyed by PolicyColumn.key
  labels: DeviceLabel[]; // for the 'labels' cell
  monitoredPaths?: string[]; // for the 'paths' cell (FIM)
  raw: DevicePolicyItem; // source policy item for edit/delete dispatch
}

export interface DevicePolicySettingsState {
  fileIntegrityPolicies: DevicePolicyFileIntegrityItem[];
  showSkeletonLoader: boolean;
  loading: boolean;
}

const defaultDevicePolicySettingsState = {
  fileIntegrityPolicies: [],
  showSkeletonLoader: true,
  loading: false,
};

export const useDevicePolicyStore = defineStore(StoreModule.DEVICE_POLICY_SETTINGS, {
  state: (): DevicePolicySettingsState => ({ ...defaultDevicePolicySettingsState }),
  getters: {
    fileIntegrityTableItems(state): DevicePolicyAdaptedItem[] {
      return mapFileIntegrityPoliciesToTableItems(state.fileIntegrityPolicies);
    },
    policyTableItems(): DevicePolicyAdaptedItem[] {
      return [...this.fileIntegrityTableItems];
    },
  },
  actions: {
    async getAllDevicePolicies(showSkeletonLoader = false) {
      this.loading = true;
      this.showSkeletonLoader = showSkeletonLoader;

      try {
        const { data } = await axiosInstance.request({ ...api.getDevicePolicies });
        this.fileIntegrityPolicies = mapFileIntegrityResponseToItems(data.fileIntegrity ?? []);
      } catch (error) {
        console.error(error);
      }

      this.loading = false;
      this.showSkeletonLoader = false;
    },
    async addDevicePolicy(payload: DevicePolicyItem) {
      const request = getAddRequestConfiguration(payload);
      try {
        await axiosInstance.request(request as AxiosRequestConfig);
        await this.getAllDevicePolicies();
      } catch (error) {
        console.error(error);
      }
    },
    async editDevicePolicy(payload: DevicePolicyItem) {
      const request = getEditRequestConfiguration(payload);
      try {
        await axiosInstance.request(request as AxiosRequestConfig);
        await this.getAllDevicePolicies();
      } catch (error) {
        console.error(error);
      }
    },
    async deleteDevicePolicy(payload: DevicePolicyItem) {
      const request = getDeleteRequestConfiguration(payload);
      try {
        await axiosInstance.request(request);
        await this.getAllDevicePolicies();
      } catch (error) {
        console.error(error);
      }
    },
  },
});

function getAddRequestConfiguration(payload: DevicePolicyItem) {
  const base = api.addDevicePolicy(DevicePolicyType.FILE_INTEGRITY);
  return {
    ...base,
    data: convertFileIntegrityPolicyForBackend(payload),
  };
}

function getEditRequestConfiguration(payload: DevicePolicyItem) {
  const base = api.updateDevicePolicy(DevicePolicyType.FILE_INTEGRITY, payload.id!);
  return {
    ...base,
    data: convertFileIntegrityPolicyForBackend(payload),
  };
}

function getDeleteRequestConfiguration(payload: DevicePolicyItem) {
  return { ...api.deleteDevicePolicy(DevicePolicyType.FILE_INTEGRITY, payload.id!) };
}
